/**
 * Animal bodies.
 *
 * The game is called Animal Attack, so the animals have to look like animals. Every
 * species gets a real silhouette — head, snout, ears, four legs, tail — built from
 * primitives at load time, because the project ships as one self-contained bundle with
 * no model files to download.
 *
 * Legs are kept as named references so they can be swung while walking; a walking wolf
 * has to read as walking.
 */
import * as THREE from 'three'
import { SPECIES, type SpeciesId } from './species'
import { coatFor, createPelt } from './pelt'
import { loftBody, loftLimb, type SpineNode } from './loft'
import { buildFurShells } from './fur'

export interface AnimalModel {
  group: THREE.Group
  /** Blended pose state, held between frames so transitions are smooth. */
  pose?: { headDown: number; crouch: number; attack: number; hurt: number }
  /** The fur shells, so they can be dropped by distance. */
  shells: THREE.Mesh[]
  /** Front-left, front-right, back-left, back-right — whichever the body has. */
  legs: THREE.Object3D[]
  tail: THREE.Object3D | null
  head: THREE.Object3D | null
  /** Wings, for the bird. */
  wings: THREE.Object3D[]
  /** Metres from the ground to the belly, so the body sits correctly. */
  rideHeight: number
}

function mat(color: number, roughness = 0.85): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, vertexColors: true })
}

/**
 * A coated material. Kate asked for real animals rather than cartoons, and a flat solid
 * colour was the single biggest thing making them look drawn — so bodies wear generated
 * fur, scales, feathers or hide, with a normal map so the sun catches it.
 */
/**
 * Geometry budget, chosen by device.
 *
 * A full field of furred animals came to 2.4M triangles and 392 draw calls a frame.
 * That is fine on a desktop GPU and would not hold up on the iPad this is mainly played
 * on, so touch devices get a lighter body and no shell fur — the coat texture still
 * carries the detail there.
 */
const IS_TOUCH = typeof window !== 'undefined' &&
  (window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0)

export const QUALITY = {
  bodySegments: IS_TOUCH ? 64 : 96,
  bodyRadial: IS_TOUCH ? 28 : 40,
  shellFur: !IS_TOUCH,
}

const peltCache = new Map<string, ReturnType<typeof createPelt>>()
function coated(speciesId: string, color: number, roughness = 0.82): THREE.MeshStandardMaterial {
  const key = `${speciesId}:${color}`
  let pelt = peltCache.get(key)
  if (!pelt) {
    pelt = createPelt(color, coatFor(speciesId), color ^ 0x9e3779b9)
    peltCache.set(key, pelt)
  }
  const coat = coatFor(speciesId)
  return new THREE.MeshStandardMaterial({
    map: pelt.map,
    normalMap: pelt.normalMap,
    // The lofted bodies bake ambient occlusion into vertex colours — undersides and
    // crevices sit in shadow, which is much of what makes a solid form read as real.
    vertexColors: true,
    normalScale: new THREE.Vector2(coat === 'smooth' ? 0.4 : 1.3, coat === 'smooth' ? 0.4 : 1.3),
    // Wet skin is shiny, but at 0.32 roughness under a 3.0 sun and a 3.3 sky the
    // highlight saturated and a grey-blue dolphin rendered near-white --- the texture
    // itself measures exactly the colour it was given. Metalness is zero because skin
    // is not metal; it was only there to fake the sheen.
    roughness: coat === 'smooth' ? 0.55 : roughness,
    metalness: 0,
  })
}

/**
 * Give a primitive a white vertex-colour attribute. The coated materials declare
 * `vertexColors` for the lofted bodies' baked occlusion, and any geometry without the
 * attribute renders pure black — which is exactly what happened to every ear and eye.
 */
function withVertexColors(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!geometry.getAttribute('color')) {
    const count = geometry.getAttribute('position').count
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(count * 3).fill(1), 3))
  }
  return geometry
}

function box(w: number, h: number, d: number, m: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(withVertexColors(new THREE.BoxGeometry(w, h, d)), m)
  mesh.castShadow = true
  return mesh
}

function capsule(r: number, len: number, m: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(withVertexColors(new THREE.CapsuleGeometry(r, len, 10, 20)), m)
  mesh.castShadow = true
  return mesh
}

function sphere(r: number, m: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(withVertexColors(new THREE.SphereGeometry(r, 22, 16)), m)
  mesh.castShadow = true
  return mesh
}

function cone(r: number, h: number, m: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(withVertexColors(new THREE.ConeGeometry(r, h, 16)), m)
  mesh.castShadow = true
  return mesh
}

/**
 * A leg pivoted at the shoulder, jointed at the knee.
 *
 * A single straight taper from shoulder to foot reads as a post: no animal's leg is
 * straight. The upper segment hangs from the shoulder, the lower one hangs from a knee
 * group set part-way down and rests at an angle — forward on the hind legs, back on the
 * front, which is the Z every quadruped stands in. The knee group is left on the pivot
 * so the gait can flex it with the stride.
 */
function leg(thickness: number, length: number, m: THREE.Material, front: boolean): THREE.Group {
  const pivot = new THREE.Group()
  const upperLength = length * 0.52
  const lowerLength = length - upperLength

  const upper = new THREE.Mesh(
    loftLimb(thickness * 1.5, thickness * 1.05, thickness * 0.9, upperLength, IS_TOUCH ? 12 : 20),
    m,
  )
  upper.castShadow = true
  upper.receiveShadow = true
  pivot.add(upper)

  const knee = new THREE.Group()
  knee.position.y = -upperLength
  // Front legs fold back at the wrist, hind legs forward at the hock.
  knee.rotation.x = front ? 0.26 : -0.3
  const lower = new THREE.Mesh(
    loftLimb(thickness * 0.92, thickness * 0.62, thickness * 0.8, lowerLength, IS_TOUCH ? 10 : 16),
    m,
  )
  lower.castShadow = true
  lower.receiveShadow = true
  knee.add(lower)
  pivot.add(knee)

  pivot.userData.knee = knee
  pivot.userData.kneeRest = knee.rotation.x
  return pivot
}

interface QuadrupedOptions {
  speciesId: string
  bodyLength: number
  bodyRadius: number
  legLength: number
  legThickness: number
  neckLength: number
  headSize: number
  snoutLength: number
  /** 'pointed' wolf/cat, 'round' bear, 'long' bunny, 'floppy' dog, 'none'. */
  ears: 'pointed' | 'round' | 'long' | 'floppy' | 'none'
  tail: 'bushy' | 'thin' | 'puff' | 'flag' | 'none'
  /**
   * Multiplier on the tail's length. Measured against real animals the tails were out
   * by up to 283% (the bear, which barely has one) and -75% (the chameleon, whose tail
   * is as long as its body), so one shape rule could never fit them all.
   */
  tailScale?: number
  color: number
  bellyColor: number
  /** Deer only. */
  antlers?: boolean
  /** Camel only. */
  hump?: boolean
  /** Short-coated species opt out of the shell fur. */
  furry?: boolean
  /** How long the coat stands off the skin. */
  furDepth?: number
}

/** The shared four-legged body plan every land animal is a variation of. */
function quadruped(o: QuadrupedOptions): AnimalModel {
  const group = new THREE.Group()
  const skin = coated(o.speciesId, o.color)
  const belly = coated(o.speciesId, o.bellyColor)
  const dark = mat(0x1b1712, 0.6)

  // One continuous body from nose to tail, rather than a head ball on a neck tube on a
  // body capsule. The seams between those parts were the cartoon look.
  const L = o.bodyLength
  const R = o.bodyRadius
  const headR = o.headSize
  const shoulderY = o.legLength + R * 0.15
  const headY = o.legLength + R * 0.25 + o.neckLength * 0.8

  const spine: SpineNode[] = [
    // Nose. A muzzle needs real thickness: at 0.3 of head width, a long-snouted animal
    // like the camel swept out a flat blade instead of a face --- from the side it read
    // as a plank nailed to the head.
    { t: 0.00, y: headY - headR * 0.28, z: -(L * 0.5 + o.snoutLength + headR * 0.9), width: headR * 0.46, height: headR * 0.42 },
    // Muzzle
    { t: 0.06, y: headY - headR * 0.2,  z: -(L * 0.5 + headR * 0.95), width: headR * 0.6, height: headR * 0.56 },
    // Skull
    { t: 0.13, y: headY,                z: -(L * 0.5 + headR * 0.25), width: headR * 0.95, height: headR * 0.98 },
    // Throat / top of neck — a neck is narrow across and deep front-to-back too.
    { t: 0.22, y: headY - o.neckLength * 0.42, z: -(L * 0.5) + o.neckLength * 0.1, width: R * 0.44, height: R * 0.66 },
    // Shoulder. Every mammal is deeper than it is wide — a barrel of circular section
    // reads as a toy from behind, which is exactly how the deer looked. Chest sections
    // are narrowed across and deepened top-to-bottom to the real proportion.
    { t: 0.34, y: shoulderY + R * 0.16, z: -L * 0.3, width: R * 0.82, height: R * 1.24 },
    // Ribcage — deepest point, as on a real animal
    { t: 0.48, y: shoulderY + R * 0.12, z: -L * 0.05, width: R * 0.86, height: R * 1.3 },
    // Waist — tucked in behind the ribs
    { t: 0.64, y: shoulderY + R * 0.04, z: L * 0.2, width: R * 0.68, height: R * 1.0 },
    // Haunch — wider again, because the hind leg muscle sits proud of the waist
    { t: 0.78, y: shoulderY + R * 0.1,  z: L * 0.4, width: R * 0.88, height: R * 1.14 },
    // Rump
    { t: 0.90, y: shoulderY + R * 0.04, z: L * 0.52, width: R * 0.58, height: R * 0.78 },
    // Tail base
    { t: 1.00, y: shoulderY + R * 0.1,  z: L * 0.6, width: R * 0.2, height: R * 0.22 },
  ]

  const bodyGeometry = loftBody(spine, {
    // Dense enough that the silhouette is a smooth curve at any distance, not a
    // faceted tube. Triangles are cheap; the low count was timidity, not a limit.
    segments: QUALITY.bodySegments, radial: QUALITY.bodyRadial, bellyFlatten: 0.26, occlusion: 0.5,
  })
  const bodyMesh = new THREE.Mesh(bodyGeometry, skin)
  bodyMesh.castShadow = true
  bodyMesh.receiveShadow = true
  group.add(bodyMesh)

  // Shell fur: the body drawn again and again, each copy pushed further along its own
  // normals with more of it cut away, so the eye reads standing strands. This is the
  // technique real game animals use — a texture alone always looks painted on.
  const shells: THREE.Mesh[] = (o.furry === false || !QUALITY.shellFur) ? [] : buildFurShells(bodyGeometry, {
    color: o.color, depth: o.furDepth ?? 0.05, seed: o.color,
  })
  for (const shell of shells) group.add(shell)

  if (o.hump) {
    const hump = sphere(R * 0.85, skin)
    hump.position.set(0, shoulderY + R * 0.85, -L * 0.02)
    hump.scale.set(1, 0.8, 1.25)
    group.add(hump)
  }

  const head = new THREE.Group()
  head.position.set(0, headY, -(L * 0.5 + headR * 0.4))

  // The skull and muzzle are part of the swept body now; only features attach here.
  const nose = sphere(headR * 0.15, dark)
  nose.position.set(0, -headR * 0.24, -headR * 0.52 - o.snoutLength * 0.9)
  head.add(nose)

  for (const side of [-1, 1]) {
    // Animals show almost no white, and their eyes sit in the skull rather than on it.
    // A white ball with a pupil standing proud of it is the single most cartoon thing
    // a model can have — so: one small dark glossy eye, set back into the head, with a
    // pinpoint catchlight to keep it from reading as a hole.
    const eyeball = sphere(headR * 0.115, mat(0x120d09, 0.12))
    eyeball.position.set(side * headR * 0.5, headR * 0.17, -headR * 0.44)
    head.add(eyeball)
    const glint = sphere(headR * 0.032, mat(0xf6f4ef, 0.05))
    glint.position.set(side * headR * 0.54, headR * 0.21, -headR * 0.52)
    head.add(glint)

    if (o.ears !== 'none') {
      let ear: THREE.Mesh
      if (o.ears === 'pointed') {
        ear = cone(headR * 0.21, headR * 0.62, skin)
        ear.position.set(side * headR * 0.46, headR * 0.74, headR * 0.05)
        // Splayed slightly, rather than two identical spikes standing to attention.
        ear.rotation.z = side * 0.2
      } else if (o.ears === 'round') {
        ear = sphere(headR * 0.32, skin)
        ear.position.set(side * headR * 0.68, headR * 0.68, headR * 0.08)
      } else if (o.ears === 'long') {
        ear = capsule(headR * 0.18, headR * 1.8, skin)
        ear.position.set(side * headR * 0.4, headR * 1.3, headR * 0.08)
        ear.rotation.z = side * 0.16
      } else {
        ear = capsule(headR * 0.22, headR * 0.85, skin)
        ear.position.set(side * headR * 0.68, headR * 0.18, headR * 0.04)
        ear.rotation.z = side * 0.5
      }
      head.add(ear)
    }

    if (o.antlers) {
      const antlerMat = mat(0x8a7a5c, 0.9)
      const beam = capsule(headR * 0.105, headR * 1.5, antlerMat)
      beam.position.set(side * headR * 0.38, headR * 1.3, 0)
      beam.rotation.z = side * 0.35
      // A brow tine sweeping forward off the base of the beam. Two bare beams read as
      // twigs stuck in a head; the forward tine is what makes the shape an antler.
      const brow = capsule(headR * 0.06, headR * 0.62, antlerMat)
      brow.position.set(side * headR * 0.5, headR * 1.02, -headR * 0.42)
      brow.rotation.set(-0.85, 0, side * 0.5)
      head.add(brow)
      head.add(beam)
      for (const [ty, tz, tilt] of [[0.7, -0.35, 0.9], [1.15, 0.3, 0.5]] as const) {
        const tine = capsule(headR * 0.065, headR * 0.68, antlerMat)
        tine.position.set(side * headR * (0.38 + ty * 0.34), headR * (1.26 + ty * 0.48), headR * tz)
        tine.rotation.z = side * tilt
        head.add(tine)
      }
    }
  }
  group.add(head)

  // Four legs.
  const legs: THREE.Object3D[] = []
  // Legs hang under the ribs, which are now narrower across than they were.
  const spreadX = o.bodyRadius * 0.62
  const spreadZ = o.bodyLength * 0.36
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const limb = leg(o.legThickness, o.legLength, skin, sz < 0)
    limb.position.set(sx * spreadX, o.legLength, sz * spreadZ)
    group.add(limb)
    legs.push(limb)
  }

  // Tail.
  let tail: THREE.Object3D | null = null
  if (o.tail !== 'none') {
    const pivot = new THREE.Group()
    pivot.position.set(0, o.legLength + o.bodyRadius * 0.4, o.bodyLength * 0.46)
    if (o.tail === 'bushy') {
      // Angled down and back. These were held out near horizontal and half a body
      // long, so a wolf trailed a plank behind it.
      const t = capsule(o.bodyRadius * 0.3, o.bodyLength * 0.4 * (o.tailScale ?? 1), skin)
      t.rotation.x = -2.35
      t.position.set(0, -o.bodyRadius * 0.1, o.bodyLength * 0.15)
      pivot.add(t)
    } else if (o.tail === 'thin') {
      // A camel's tail is a thin switch that hangs down, not a 93cm rod held out level.
      const t = capsule(o.bodyRadius * 0.11, o.bodyLength * 0.34 * (o.tailScale ?? 1), skin)
      t.rotation.x = -2.85
      t.position.set(0, -o.bodyRadius * 0.05, o.bodyLength * 0.06)
      pivot.add(t)
    } else if (o.tail === 'flag') {
      // A deer's tail is a short flag held against the rump, pale underneath. It had
      // been sharing the rabbit's puff, which put a 27cm white ball on a deer.
      const t = cone(o.bodyRadius * 0.2, o.bodyRadius * 0.85 * (o.tailScale ?? 1), belly)
      t.rotation.x = Math.PI * 0.86
      t.position.set(0, o.bodyRadius * 0.16, o.bodyLength * 0.1)
      pivot.add(t)
    } else {
      // A rabbit's scut: small, round, and only a rabbit's.
      const t = sphere(o.bodyRadius * 0.34 * (o.tailScale ?? 1), belly)
      t.position.set(0, o.bodyRadius * 0.1, o.bodyLength * 0.12)
      pivot.add(t)
    }
    group.add(pivot)
    tail = pivot
  }

  return { group, shells, legs, tail, head, wings: [], rideHeight: o.legLength }
}

/** The bird: small body, wings that flap, a beak, tail feathers. */
function bird(color: number): AnimalModel {
  const group = new THREE.Group()
  // The bird was built at 1.4m long --- longer than the cat and nearly a wolf, for the
  // species declared second-smallest of the thirteen. Its parts are laid out below at
  // that original size and the whole bird is scaled down here, which keeps the legs,
  // wings and gait in proportion. Lands at roughly crow scale: small enough to be a
  // bird, big enough to see and to play as.
  group.scale.setScalar(0.46)
  const skin = coated('bird', color)
  const beakMat = mat(0xe0a83c, 0.6)
  const dark = mat(0x14110d, 0.5)

  const body = capsule(0.26, 0.42, skin)
  body.rotation.x = Math.PI / 2.3
  body.position.y = 0.42
  group.add(body)

  const head = new THREE.Group()
  head.position.set(0, 0.72, -0.2)
  const skull = sphere(0.19, skin)
  head.add(skull)
  const beak = cone(0.09, 0.26, beakMat)
  beak.rotation.x = -Math.PI / 2
  beak.position.set(0, -0.01, -0.24)
  head.add(beak)
  for (const side of [-1, 1]) {
    const eye = sphere(0.05, dark)
    eye.position.set(side * 0.11, 0.05, -0.13)
    head.add(eye)
  }
  group.add(head)

  const wings: THREE.Object3D[] = []
  for (const side of [-1, 1]) {
    const pivot = new THREE.Group()
    pivot.position.set(side * 0.2, 0.5, 0)
    const wing = box(0.5, 0.05, 0.34, skin)
    wing.position.x = side * 0.25
    pivot.add(wing)
    group.add(pivot)
    wings.push(pivot)
  }

  const tail = new THREE.Group()
  tail.position.set(0, 0.44, 0.26)
  const feathers = box(0.26, 0.04, 0.34, skin)
  feathers.position.z = 0.16
  tail.add(feathers)
  group.add(tail)

  const legs: THREE.Object3D[] = []
  for (const side of [-1, 1]) {
    // A bird's leg folds forward at the hock, like a quadruped's hind leg.
    const limb = leg(0.035, 0.24, beakMat, false)
    limb.position.set(side * 0.09, 0.26, 0.02)
    group.add(limb)
    legs.push(limb)
  }

  return { group, shells: [], legs, tail, head, wings, rideHeight: 0.26 }
}

/** Ocean bodies: streamlined, dorsal fin, tail fluke, no legs. */
function swimmer(speciesId: string, color: number, length: number, chunky: boolean): AnimalModel {
  const group = new THREE.Group()
  const skin = coated(speciesId, color, 0.3)
  const belly = mat(0xd8dde0, 0.4)

  const body = capsule(length * 0.17, length * 0.66, skin)
  body.rotation.x = Math.PI / 2
  body.position.y = length * 0.22
  body.scale.set(1, 1, chunky ? 1.15 : 0.92)
  group.add(body)

  const underside = capsule(length * 0.12, length * 0.5, belly)
  underside.rotation.x = Math.PI / 2
  underside.position.set(0, length * 0.15, 0)
  group.add(underside)

  const head = new THREE.Group()
  head.position.set(0, length * 0.22, -length * 0.42)
  const snout = cone(length * 0.15, length * 0.3, skin)
  snout.rotation.x = -Math.PI / 2
  head.add(snout)
  for (const side of [-1, 1]) {
    const eye = sphere(length * 0.035, mat(0x14110d, 0.4))
    eye.position.set(side * length * 0.1, length * 0.04, length * 0.04)
    head.add(eye)
  }
  group.add(head)

  const dorsal = cone(length * 0.1, length * 0.3, skin)
  dorsal.position.set(0, length * 0.4, length * 0.02)
  dorsal.scale.z = 0.35
  // Raked back, the way a real dorsal is — upright it read as a sail.
  dorsal.rotation.x = 0.34
  group.add(dorsal)

  const tail = new THREE.Group()
  tail.position.set(0, length * 0.22, length * 0.38)
  // A fluke is two swept blades, not a rectangular plank. Built from cones so each
  // half tapers to a point and sweeps back, which is the shape that reads as a tail
  // rather than as a board nailed across the back of a boat.
  for (const side of [-1, 1]) {
    const blade = cone(length * 0.075, length * 0.26, skin)
    blade.rotation.set(Math.PI / 2, 0, side * (Math.PI / 2 - 0.42))
    blade.position.set(side * length * 0.12, 0, length * 0.14)
    blade.scale.y = 1
    blade.scale.z = 0.28
    tail.add(blade)
  }
  group.add(tail)

  const legs: THREE.Object3D[] = []
  for (const side of [-1, 1]) {
    // Pectoral flippers: tapered and swept back, rather than two flat plates.
    const fin = cone(length * 0.06, length * 0.24, skin)
    fin.position.set(side * length * 0.17, length * 0.16, -length * 0.1)
    fin.rotation.set(0.9, 0, side * (Math.PI / 2 - 0.55))
    fin.scale.z = 0.3
    fin.castShadow = true
    group.add(fin)
    legs.push(fin)
  }

  return { group, shells: [], legs, tail, head, wings: [], rideHeight: length * 0.1 }
}

/**
 * Skin. The same generated coat as the body, but with the grain turned right down: at the
 * scale of a face, the stroke detail that reads as skin on an arm reads as cork.
 */
function skinMaterial(color: number): THREE.MeshStandardMaterial {
  const key = `skin:${color}`
  let pelt = peltCache.get(key)
  if (!pelt) {
    pelt = createPelt(color, 'short', color ^ 0x51ab3d17)
    peltCache.set(key, pelt)
  }
  return new THREE.MeshStandardMaterial({
    map: pelt.map,
    normalMap: pelt.normalMap,
    normalScale: new THREE.Vector2(0.3, 0.3),
    vertexColors: true,
    roughness: 0.58,
  })
}

/** Cloth: the shirt and trousers get a woven texture, not a flat plastic colour. */
function clothMaterial(color: number): THREE.MeshStandardMaterial {
  const key = `cloth:${color}`
  let pelt = peltCache.get(key)
  if (!pelt) {
    pelt = createPelt(color, 'cloth', color ^ 0x2545f6a3)
    peltCache.set(key, pelt)
  }
  return new THREE.MeshStandardMaterial({
    map: pelt.map,
    normalMap: pelt.normalMap,
    normalScale: new THREE.Vector2(0.9, 0.9),
    vertexColors: true,
    roughness: 0.92,
  })
}

/**
 * An upright lofted section — the torso. loftBody sweeps its ellipse along Z, so the
 * mesh is turned to stand up; without that a human's chest is built lying down.
 */
function upright(nodes: SpineNode[], material: THREE.Material, radial: number): THREE.Mesh {
  // Almost no baked occlusion: loftBody darkens the section's lower half on the
  // assumption that it is an animal's belly, and standing the section up turns that into
  // a dark smear down the human's back.
  const mesh = new THREE.Mesh(
    loftBody(nodes, { segments: 36, radial, bellyFlatten: 0.12, occlusion: 0.07 }), material)
  mesh.rotation.x = -Math.PI / 2
  mesh.castShadow = true
  return mesh
}

/** The human: bipedal, so it reads as clearly not-an-animal. */
function human(): AnimalModel {
  const group = new THREE.Group()
  const skin = skinMaterial(0xc39a7c)
  const shirt = clothMaterial(0x4a6b8a)
  const trousers = clothMaterial(0x3d4450)
  const dark = mat(0x2a2018, 0.7)
  const radial = IS_TOUCH ? 14 : 20

  // One continuous torso: hips, a narrower waist, a broader chest, then shoulders. The
  // ends taper to nothing inside the trousers and the neck, where nobody sees them.
  const torso = upright([
    { t: 0, y: 0, z: 0.02, width: 0.09, height: 0.075 },
    { t: 0.16, y: 0, z: 0.10, width: 0.155, height: 0.115 },
    { t: 0.42, y: 0, z: 0.24, width: 0.142, height: 0.098 },
    { t: 0.72, y: 0, z: 0.40, width: 0.195, height: 0.125 },
    { t: 0.9, y: 0, z: 0.48, width: 0.215, height: 0.115 },
    { t: 1, y: 0, z: 0.54, width: 0.07, height: 0.06 },
  ], shirt, radial)
  torso.position.y = 0.86
  group.add(torso)

  // Hips, so the trousers meet the shirt instead of leaving a seam.
  const hips = upright([
    { t: 0, y: 0, z: 0, width: 0.08, height: 0.07 },
    { t: 0.4, y: 0, z: 0.09, width: 0.155, height: 0.115 },
    { t: 1, y: 0, z: 0.2, width: 0.1, height: 0.09 },
  ], trousers, radial)
  hips.position.y = 0.8
  group.add(hips)

  const head = new THREE.Group()
  head.position.y = 1.55
  // A neck: the old head floated a clear gap above the shoulders.
  const neck = new THREE.Mesh(loftLimb(0.055, 0.052, 0.075, 0.12, radial), skin)
  neck.position.y = -0.04
  neck.castShadow = true
  head.add(neck)
  // The skull is an egg rather than a ball — taller than wide, with a jaw below.
  const skull = sphere(0.115, skin)
  skull.scale.set(1, 1.18, 1.08)
  skull.position.y = 0.075
  head.add(skull)
  const jaw = sphere(0.082, skin)
  jaw.scale.set(1, 0.78, 1.05)
  jaw.position.set(0, 0.005, -0.028)
  head.add(jaw)
  // Hair covers the skull rather than sitting on it like a bowl, and gets the same shell
  // treatment as animal fur so its edge is soft instead of a hard rim.
  const hairGeo = new THREE.SphereGeometry(0.125, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.66)
  withVertexColors(hairGeo)
  const hair = new THREE.Mesh(hairGeo, dark)
  hair.scale.set(1.04, 1.22, 1.1)
  hair.position.set(0, 0.055, 0.006)
  hair.castShadow = true
  head.add(hair)
  for (const shell of buildFurShells(hairGeo, { color: 0x2a2018, seed: 7, depth: 0.012, tiling: 20 })) {
    shell.scale.copy(hair.scale)
    shell.position.copy(hair.position)
    head.add(shell)
  }
  // Eyes: white, with a dark pupil in front of it, standing proud of the skull. Kate:
  // "my character doesn't have any eyes" — the old ones were flush and invisible.
  const white = new THREE.MeshStandardMaterial({ color: 0xf4f1ea, roughness: 0.18, vertexColors: true })
  const wet = new THREE.MeshStandardMaterial({ color: 0x120d08, roughness: 0.06, vertexColors: true })
  for (const side of [-1, 1]) {
    const eyeball = sphere(0.032, white)
    eyeball.position.set(side * 0.05, 0.075, -0.098)
    head.add(eyeball)
    // A glossy pupil, because a dry matte eye is the thing that reads as a doll.
    const pupil = sphere(0.017, wet)
    pupil.position.set(side * 0.052, 0.075, -0.117)
    head.add(pupil)
    const brow = box(0.055, 0.014, 0.018, dark)
    brow.position.set(side * 0.05, 0.115, -0.104)
    head.add(brow)
  }
  const nose = sphere(0.022, skin)
  nose.scale.set(0.9, 1.1, 1.3)
  nose.position.set(0, 0.04, -0.115)
  head.add(nose)
  const mouth = box(0.05, 0.011, 0.016, dark)
  mouth.position.set(0, -0.025, -0.108)
  head.add(mouth)
  group.add(head)

  const legs: THREE.Object3D[] = []
  for (const side of [-1, 1]) {
    // A short sleeve rather than a ball joint: a sphere on the shoulder read as a puff.
    const sleeve = new THREE.Mesh(loftLimb(0.075, 0.066, 0.058, 0.13, radial), shirt)
    sleeve.position.set(side * 0.196, 1.345, 0)
    sleeve.castShadow = true
    group.add(sleeve)

    // An elbow folds back, the same direction a front leg's wrist does.
    const arm = leg(0.05, 0.48, skin, true)
    arm.position.set(side * 0.198, 1.255, 0)
    group.add(arm)
    legs.push(arm)
    // Hands, or the arms read as pegs.
    const hand = sphere(0.045, skin)
    hand.scale.set(0.85, 1.15, 0.7)
    hand.position.set(0, -0.48, 0)
    arm.add(hand)

    // A human knee folds back too.
    const limb = leg(0.055, 0.82, trousers, true)
    limb.position.set(side * 0.082, 0.82, 0)
    group.add(limb)
    legs.push(limb)
    // A shoe, which is what actually makes a leg look like it stands on the ground.
    const foot = box(0.082, 0.052, 0.19, dark)
    foot.position.set(0, -0.8, -0.045)
    limb.add(foot)
  }

  return { group, shells: [], legs, tail: null, head, wings: [], rideHeight: 0.86 }
}

/** Build the body for a species. */
export function buildAnimal(id: SpeciesId): AnimalModel {
  const color = SPECIES[id].color
  switch (id) {
    case 'human':
      return human()
    case 'bear':
      return quadruped({ speciesId: 'bear', furDepth: 0.09, bodyLength: 1.5, bodyRadius: 0.56, legLength: 0.895, legThickness: 0.19,
        neckLength: 0.3, headSize: 0.198, snoutLength: 0.156, ears: 'round', tail: 'puff', tailScale: 0.218,
        color, bellyColor: 0x3a2a1e })
    case 'wolf':
      return quadruped({ speciesId: 'wolf', furDepth: 0.055, bodyLength: 1.25, bodyRadius: 0.34, legLength: 1.045, legThickness: 0.11,
        neckLength: 0.32, headSize: 0.223, snoutLength: 0.281, ears: 'pointed', tail: 'bushy', tailScale: 1.15,
        color, bellyColor: 0x8b8478 })
    case 'dog':
      return quadruped({ speciesId: 'dog', furDepth: 0.045, bodyLength: 0.95, bodyRadius: 0.28, legLength: 0.779, legThickness: 0.09,
        neckLength: 0.24, headSize: 0.185, snoutLength: 0.21, ears: 'floppy', tail: 'bushy', tailScale: 1.37,
        color, bellyColor: 0xb99a72 })
    case 'cat':
      return quadruped({ speciesId: 'cat', furDepth: 0.03, bodyLength: 0.7, bodyRadius: 0.2, legLength: 0.439, legThickness: 0.06,
        neckLength: 0.16, headSize: 0.137, snoutLength: 0.092, ears: 'pointed', tail: 'thin', tailScale: 2.5,
        color, bellyColor: 0xc4b49a })
    case 'bunny':
      return quadruped({ speciesId: 'bunny', furDepth: 0.045, bodyLength: 0.44, bodyRadius: 0.2, legLength: 0.317, legThickness: 0.06,
        neckLength: 0.08, headSize: 0.122, snoutLength: 0.065, ears: 'long', tail: 'puff', tailScale: 0.673,
        color, bellyColor: 0xded3c4 })
    case 'deer':
      return quadruped({ speciesId: 'deer', furDepth: 0.022, bodyLength: 1.2, bodyRadius: 0.32, legLength: 0.813, legThickness: 0.08,
        neckLength: 0.46, headSize: 0.155, snoutLength: 0.183, ears: 'pointed', tail: 'flag', tailScale: 0.67,
        color, bellyColor: 0xcbb593, antlers: true })
    case 'camel':
      return quadruped({ speciesId: 'camel', furry: false, bodyLength: 1.5, bodyRadius: 0.44, legLength: 1.151, legThickness: 0.11,
        neckLength: 0.72, headSize: 0.21, snoutLength: 0.235, ears: 'round', tail: 'thin', tailScale: 0.569,
        color, bellyColor: 0xd9c093, hump: true })
    case 'chameleon':
      return quadruped({ speciesId: 'chameleon', furry: false, bodyLength: 0.5, bodyRadius: 0.13, legLength: 0.245, legThickness: 0.04,
        neckLength: 0.07, headSize: 0.174, snoutLength: 0.134, ears: 'none', tail: 'thin', tailScale: 5.195,
        color, bellyColor: 0x86a860 })
    case 'bird':
      return bird(color)
    case 'dolphin':
      return swimmer('dolphin', color, 1.7, false)
    case 'whale':
      return swimmer('whale', color, 3.4, true)
    case 'fish':
      return swimmer('fish', color, 0.55, false)
  }
}

/**
 * Swing the legs (and flap the wings) so movement reads as movement.
 * `phase` accumulates with distance travelled, not time, so a stopped animal is still.
 */
/**
 * Drop fur shells by distance.
 *
 * Shell fur is fourteen extra draws per animal. Close up that is what makes a coat read
 * as fur; at thirty metres it is invisible and costs the same. A full field of animals
 * came to 2.4M triangles and 392 draw calls a frame, which would not hold up on the iPad
 * this is mainly played on.
 */
export function updateFurDetail(model: AnimalModel, distance: number): void {
  if (!model.shells.length) return
  // Full coat up close, thinning with distance, gone beyond 34m.
  const keep = distance > 34 ? 0
    : distance > 20 ? 3
    : distance > 11 ? 7
    : model.shells.length
  for (let i = 0; i < model.shells.length; i++) {
    // Keep an even spread of the stack rather than only the innermost ones.
    const stride = keep === 0 ? Infinity : model.shells.length / keep
    model.shells[i]!.visible = keep > 0 && Math.floor(i % stride) === 0
  }
}

/**
 * How an animal moves its legs. A single shared swing made a bunny, a bear and a cat all
 * move identically, which undercuts the whole premise of playing different animals.
 */
export type Gait = 'trot' | 'bound' | 'lumber' | 'prowl' | 'hop'

export function gaitFor(speciesId: string): Gait {
  switch (speciesId) {
    // Both hind legs together — a bunny bounds, it does not walk.
    case 'bunny': return 'bound'
    // Heavy and slow, with a long stride and a roll.
    case 'bear': case 'camel': case 'whale': return 'lumber'
    // Low and smooth, feet close to the ground.
    case 'cat': case 'chameleon': return 'prowl'
    // On the ground a bird hops rather than walks.
    case 'bird': return 'hop'
    default: return 'trot'
  }
}

export interface AnimatePose {
  /** 0..1 — how far the head is lowered, for grazing or drinking. */
  headDown?: number
  /** 0..1 — crouched low, for a wolf sniffing or a cat stalking. */
  crouch?: number
  /** 0..1 — mid-bite: weight forward, head thrust, jaws at the target. */
  attack?: number
  /** 0..1 — flinching from a hit, decaying quickly. */
  hurt?: number
}

/**
 * Poses are blended toward rather than snapped to. Switching instantly between standing,
 * grazing and biting made an animal look like it was being posed by hand; easing between
 * them is most of what makes the same three poses read as one creature.
 */
const POSE_BLEND_RATE = 9

interface PoseState {
  headDown: number
  crouch: number
  attack: number
  hurt: number
}

function blendPose(current: PoseState, target: AnimatePose, dt: number): PoseState {
  const k = 1 - Math.exp(-POSE_BLEND_RATE * dt)
  current.headDown += ((target.headDown ?? 0) - current.headDown) * k
  current.crouch += ((target.crouch ?? 0) - current.crouch) * k
  current.attack += ((target.attack ?? 0) - current.attack) * k
  // A flinch snaps on and eases off, rather than easing both ways.
  const wantHurt = target.hurt ?? 0
  current.hurt = wantHurt > current.hurt ? wantHurt : current.hurt + (wantHurt - current.hurt) * k
  return current
}

export function animate(
  model: AnimalModel, phase: number, moving: boolean, airborne: boolean,
  gait: Gait = 'trot', pose: AnimatePose = {}, dt = 1 / 60,
): void {
  // Blend toward the requested pose instead of snapping to it.
  const held = (model.pose ??= { headDown: 0, crouch: 0, attack: 0, hurt: 0 })
  blendPose(held, pose, dt)
  const amp = moving ? 1 : 0

  model.legs.forEach((limb, i) => {
    const front = i < 2
    const left = i % 2 === 0
    let swing = 0
    let lift = 0

    switch (gait) {
      case 'trot': {
        // Diagonal pairs together — the default four-legged walk.
        const direction = i === 0 || i === 3 ? 1 : -1
        swing = Math.sin(phase) * 0.6 * direction
        break
      }
      case 'bound': {
        // Front pair reaches, then the back pair pushes off — nearly opposite phase, which
        // is what reads as a bound. A smaller offset just looked like a shuffle.
        const offset = front ? 0 : Math.PI * 0.9
        swing = Math.sin(phase + offset) * 0.75
        // Both feet leave the ground together at the top of the bound.
        lift = Math.max(0, Math.sin(phase + offset)) * 0.14
        break
      }
      case 'lumber': {
        // Slow, wide, and out of step side to side, so the body rolls.
        const direction = left ? 1 : -1
        swing = Math.sin(phase * 0.62 + (front ? 0 : 0.7)) * 0.42 * direction
        break
      }
      case 'prowl': {
        // Small, quick, low steps.
        const direction = i === 0 || i === 3 ? 1 : -1
        swing = Math.sin(phase * 1.25) * 0.3 * direction
        break
      }
      case 'hop': {
        // Both legs together, sharp and quick.
        swing = Math.max(0, Math.sin(phase * 1.6)) * 0.5
        lift = Math.max(0, Math.sin(phase * 1.6)) * 0.1
        break
      }
    }

    limb.rotation.x = swing * amp
    limb.position.y = (limb.userData.restY ??= limb.position.y) + lift * amp

    // The knee folds as the leg comes forward and straightens as it takes weight —
    // a rigid two-piece leg still reads as a hinge-less post.
    const knee = limb.userData.knee as THREE.Group | undefined
    if (knee) {
      const rest = limb.userData.kneeRest as number
      // Smoothstep rather than a bare max: clamping at zero leaves a kink where the
      // fold starts, and the knee snaps straight instead of easing off the ground.
      const raw = Math.max(0, front ? -swing : swing)
      const fold = raw * raw * (3 - 2 * Math.min(1, raw))
      knee.rotation.x = rest + (front ? fold : -fold) * 1.4 * amp
    }
  })

  // The tail counterweights the gait rather than always doing the same thing.
  if (model.tail) {
    const sway = gait === 'bound' ? 0.1 : gait === 'lumber' ? 0.34 : 0.22
    model.tail.rotation.y = moving ? Math.sin(phase * 0.5) * sway : 0
    // Tucked when crouching or flinching, as a wary or hurt animal does.
    model.tail.rotation.x = -(held.crouch * 0.4 + held.hurt * 0.5)
  }

  // Whole-body attitude: leaning into a bite, recoiling from a hit.
  model.group.rotation.x = held.attack * 0.16 - held.hurt * 0.12
  model.group.position.z = held.attack * -0.12

  // Wings flap in the air and fold on the ground.
  for (const [i, wing] of model.wings.entries()) {
    const side = i === 0 ? 1 : -1
    wing.rotation.z = airborne ? Math.sin(phase * 2.4) * 0.9 * side : 0.1 * side
  }

  // Head lowers to graze or drink, drops when crouching to sniff, thrusts forward when
  // biting, and snaps aside when hurt.
  if (model.head) {
    const rest = (model.head.userData.restY ??= model.head.position.y)
    const restZ = (model.head.userData.restZ ??= model.head.position.z)
    model.head.position.y = rest - held.headDown * 0.55 - held.crouch * 0.2 + held.attack * 0.06
    model.head.position.z = restZ - held.attack * 0.22
    model.head.rotation.x = held.headDown * 0.8 + held.crouch * 0.25 - held.attack * 0.3
    model.head.rotation.z = held.hurt * 0.35
  }
}
